import React, { useState, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import useCustomerData from '@/hooks/useCustomerData'

export default function CustomerPicker({ onCustomerSelect, onChoose }) {
	const { t } = useTranslation()
	const [searchTerm, setSearchTerm] = useState('')

	const {
		customerData,
		loadingCustomers,
		loadingMore,
		error,
		hasMore,
		loadCustomerData,
		handleLoadMore,
		updateFilters,
	} = useCustomerData()

	// Trigger fetch on mount and whenever filters/limit change through loadCustomerData deps
	useEffect(() => {
		loadCustomerData()
	}, [loadCustomerData])

	const handleSearchChange = useCallback(
		(e) => {
			const value = e.target.value
			setSearchTerm(value)

			if (value.length >= 3 || value.length === 0) {
				updateFilters({ searchText: value })
			}
		},
		[updateFilters]
	)

	const handleSelect = useCallback(
		(customer) => {
			const cb = onCustomerSelect || onChoose
			if (cb) cb(customer)
		},
		[onCustomerSelect, onChoose]
	)

	const handleKeyDown = (e) => {
		if (e.key === 'Enter') {
			e.preventDefault()
			if (customerData.length === 1) {
				handleSelect(customerData[0])
			}
		}
	}

	return (
		<div className="space-y-4">
			<div className="mb-4">
				<input
					type="text"
					value={searchTerm}
					onChange={handleSearchChange}
					onKeyDown={handleKeyDown}
					placeholder={t('customerPicker.searchPlaceholder')}
					className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
				/>
			</div>

			{error && (
				<div className="bg-red-50 border border-red-200 rounded-md p-3">
					<div className="text-sm text-red-600">{t('customerPicker.errorTitle')}: {String(error)}</div>
				</div>
			)}

			{loadingCustomers ? (
				<div className="text-center py-4 text-gray-500">{t('customerPicker.loading')}</div>
			) : customerData.length === 0 ? (
				<div className="text-center py-4 text-gray-500">{t('customerPicker.empty')}</div>
			) : (
				<div className="overflow-x-auto">
					<table className="min-w-full bg-white border border-gray-300 rounded-lg shadow">
						<thead>
							<tr className="bg-gray-50">
								<th className="py-3 px-4 border-b border-gray-300 text-left font-semibold text-gray-700">{t('customerPicker.table.name')}</th>
								<th className="py-3 px-4 border-b border-gray-300 text-left font-semibold text-gray-700">{t('customerPicker.table.email')}</th>
								<th className="py-3 px-4 border-b border-gray-300 text-left font-semibold text-gray-700">{t('customerPicker.table.phone')}</th>
								<th className="py-3 px-4 border-b border-gray-300 text-left font-semibold text-gray-700">{t('customerPicker.table.address')}</th>
								<th className="py-3 px-4 border-b border-gray-300 text-left w-24 font-semibold text-gray-700">{t('customerPicker.table.action')}</th>
							</tr>
						</thead>
						<tbody>
							{customerData.map((customer) => (
								<tr key={customer._id || customer.id} className="hover:bg-gray-50">
									<td className="py-3 px-4 border-b border-gray-300">
										<span className="font-medium text-gray-900">
											{customer.name || `${customer.firstName || ''} ${customer.lastName || ''}`.trim() || '—'}
										</span>
									</td>
									<td className="py-3 px-4 border-b border-gray-300">
										<span className="text-gray-900">{customer.email || '—'}</span>
									</td>
									<td className="py-3 px-4 border-b border-gray-300">
										<span className="text-gray-900">{customer.tel || customer.phone || '—'}</span>
									</td>
									<td className="py-3 px-4 border-b border-gray-300">
										<span className="text-gray-900 text-sm">{customer.address || '—'}</span>
									</td>
									<td className="py-3 px-4 border-b border-gray-300">
										<button
											type="button"
											onClick={() => handleSelect(customer)}
											className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
										>
											{t('customerPicker.select')}
										</button>
									</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
			)}

			{hasMore && (
				<div className="text-center">
					<button
						type="button"
						onClick={handleLoadMore}
						className="px-4 py-2 text-sm font-medium text-blue-700 border border-blue-200 rounded-md hover:bg-blue-50 disabled:opacity-60"
						disabled={loadingMore}
					>
						{loadingMore ? t('customerPicker.loadingMore') : t('customerPicker.loadMore')}
					</button>
				</div>
			)}
		</div>
	)
}
